import React, { useEffect, useState } from 'react';
import {
  Controller,
  FieldError,
  FieldValues,
  NestDataObject,
  useForm,
} from 'react-hook-form';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import HostExperienceSingleDatePicker from './HostExperienceSingleDatePicker';
import ErrorMessage from './ErrorMessage';

interface SessionDatePickerProps {
  errors: NestDataObject<FieldValues, FieldError>;
  isDayHighlighted: (date: Date) => boolean;
  onSessionDatePrevMonthClick: (date: Moment) => void;
  onSessionDateNextMonthClick: (date: Moment) => void;
  selectedSessionDateAvailabilityCalendar: Moment | null;
  setSelectedSessionDateAvailabilityCalendar: (date: Moment) => void;
  isDropdownVisible: boolean;
  isCalendarLoading: boolean;
}

const SingleSessionDatePicker: React.FC<SessionDatePickerProps> = ({
  errors,
  isDayHighlighted,
  onSessionDatePrevMonthClick,
  onSessionDateNextMonthClick,
  selectedSessionDateAvailabilityCalendar,
  setSelectedSessionDateAvailabilityCalendar,
  isDropdownVisible,
  isCalendarLoading,
}) => {
  const { control } = useForm();
  const { t: translate } = useTranslation();
  const [localDate, setLocalDate] = useState(
    selectedSessionDateAvailabilityCalendar,
  );

  useEffect(() => {
    setLocalDate(selectedSessionDateAvailabilityCalendar);
  }, [selectedSessionDateAvailabilityCalendar]);

  const handleChange = (date: Date) => {
    setSelectedSessionDateAvailabilityCalendar(moment(date));
    return date;
  };

  const value = localDate instanceof moment ? localDate.toDate() : null;

  return (
    <div className="experience-view__single-calendar-wrapper">
      <Controller
        as={
          <HostExperienceSingleDatePicker
            date={value ?? null}
            initialVisibleMonth={() => value ?? new Date()}
            onPrevMonthClick={(date) =>
              onSessionDatePrevMonthClick(moment(date))
            }
            onNextMonthClick={(date) =>
              onSessionDateNextMonthClick(moment(date))
            }
            isOutsideRange={(date) => !isDayHighlighted(date)}
            onChange={handleChange}
            isCalendarLoading={isCalendarLoading}
          />
        }
        control={control}
        rules={{ required: true }}
        onChange={([selected]) => handleChange(selected)}
        defaultValue={localDate}
        name="sessionDate"
        valueName="date"
        isDropdownOpen={isDropdownVisible}
        aria-describedby="sessionDate"
        key={localDate?.valueOf()}
      />
      <ErrorMessage
        errors={errors}
        name="sessionDate"
        message={translate('selectDateValidation')}
      />
    </div>
  );
};

export default SingleSessionDatePicker;
